/**
 * 必殺技特效系統
 * 處理角落支配、大規模翻轉、完美遊戲、逆轉等特殊招式
 */

import type { PlayerColor, BoardPosition } from '@/types/game-types';
import { ParticlePresetType, ParticlePresetConfig, PARTICLE_PRESETS, blendPresets } from './particle-presets';
import { t } from '@/i18n/index';

export type SpecialMoveType =
  | 'corner_joseki'    // 角部定式 - 经典角部布局
  | 'great_reversal'   // 大翻盘 - 乾坤大挪移，局势逆转
  | 'total_domination' // 完全压制 - 如臂使指，掌控全局
  | 'desperate_kill'   // 绝地反杀 - 绝境求生，反败为胜
  | 'perfect_endgame'  // 完美收官 - 收官如神，无懈可击
  | 'edge_control'     // 边线掌控 - 边线为王，稳扎稳打
  | 'chain_reaction'   // 连锁反应 - 环环相扣，精妙计算
  | 'time_control';    // 时间掌控 - 运筹帷幄，决胜千里

export interface SpecialMoveCondition {
  /** 翻轉棋子數量要求 */
  minFlipCount?: number;
  /** 分數差距要求 */
  scoreDifference?: { min: number; max: number };
  /** 位置要求 */
  positionType?: 'corner' | 'edge' | 'center';
  /** 時機要求 */
  timing?: 'early' | 'mid' | 'late';
  /** 特殊條件 */
  special?: string;
}

export interface SpecialMoveEffect {
  /** 特效類型 */
  type: SpecialMoveType;
  /** 粒子效果配置 */
  particleEffect: ParticlePresetConfig;
  /** 視覺效果 */
  visual: {
    screenShake: boolean;
    slowMotion: boolean;
    zoom: boolean;
    cameraMovement: boolean;
    colorFilter?: string;
  };
  /** 音效 */
  audio: {
    soundEffect: string;
    volume: number;
    echo?: boolean;
    reverb?: boolean;
  };
  /** 持續時間 */
  duration: number;
  /** 文字顯示 */
  display: {
    title: string;
    subtitle?: string;
    color: string;
    fontSize: number;
    animation: 'fade' | 'zoom' | 'slide' | 'explode';
  };
}

/**
 * 特殊招式檢測條件
 */
export const SPECIAL_MOVE_CONDITIONS: Record<SpecialMoveType, SpecialMoveCondition> = {
  corner_joseki: {
    positionType: 'corner',
    minFlipCount: 3,
    timing: 'early' as any,
  },
  great_reversal: {
    minFlipCount: 6, // 大翻盘需要至少6个棋子翻转
  },
  total_domination: {
    scoreDifference: { min: 15, max: 999 }, // 完全压制需要领先15分以上
    minFlipCount: 4, // 至少翻转4个棋子
  },
  desperate_kill: {
    scoreDifference: { min: -15, max: -1 }, // 绝地反杀需要落后15分以内
    minFlipCount: 4, // 至少翻转4个棋子实现反杀
  },
  perfect_endgame: {
    scoreDifference: { min: 20, max: 999 }, // 完美收官需要大幅领先
    special: 'near_endgame',
  },
  edge_control: {
    positionType: 'edge',
    minFlipCount: 5, // 边线掌控需要至少5个棋子翻转
  },
  chain_reaction: {
    minFlipCount: 8, // 连锁反应需要至少8个棋子翻转
    special: 'multiple_directions',
  },
  time_control: {
    minFlipCount: 10, // 时间掌控需要至少10个棋子翻转
    special: 'ultra_rare',
  },
};

/**
 * 特殊招式效果配置
 */
export const SPECIAL_MOVE_EFFECTS: Record<SpecialMoveType, SpecialMoveEffect> = {
  corner_joseki: {
    type: 'corner_joseki',
    particleEffect: blendPresets('explosion', 'magic', 0.7),
    visual: {
      screenShake: true,
      slowMotion: false,
      zoom: true,
      cameraMovement: true,
      colorFilter: '#ffd700',
    },
    audio: {
      soundEffect: 'corner_joseki',
      volume: 0.8,
      echo: true,
    },
    duration: 1500,
    display: {
      title: t('special.corner_joseki'),
      subtitle: t('special.corner_joseki.subtitle'),
      color: '#ffd700',
      fontSize: 36,
      animation: 'explode',
    },
  },

  great_reversal: {
    type: 'great_reversal',
    particleEffect: blendPresets('storm', 'lightning', 0.6),
    visual: {
      screenShake: true,
      slowMotion: true,
      zoom: true,
      cameraMovement: true,
      colorFilter: '#87ceeb',
    },
    audio: {
      soundEffect: 'great_reversal',
      volume: 0.9,
      echo: true,
      reverb: true,
    },
    duration: 2000,
    display: {
      title: t('special.great_reversal'),
      subtitle: t('special.great_reversal.subtitle'),
      color: '#87ceeb',
      fontSize: 42,
      animation: 'zoom',
    },
  },

  total_domination: {
    type: 'total_domination',
    particleEffect: blendPresets('blackhole', 'magic', 0.8),
    visual: {
      screenShake: true,
      slowMotion: true,
      zoom: false,
      cameraMovement: false,
      colorFilter: '#9932cc',
    },
    audio: {
      soundEffect: 'total_domination',
      volume: 1.0,
      echo: true,
      reverb: true,
    },
    duration: 2500,
    display: {
      title: t('special.total_domination'),
      subtitle: t('special.total_domination.subtitle'),
      color: '#9932cc',
      fontSize: 48,
      animation: 'explode',
    },
  },

  desperate_kill: {
    type: 'desperate_kill',
    particleEffect: blendPresets('fire', 'explosion', 0.8),
    visual: {
      screenShake: true,
      slowMotion: true,
      zoom: true,
      cameraMovement: true,
      colorFilter: '#ff4500',
    },
    audio: {
      soundEffect: 'desperate_kill',
      volume: 0.95,
      echo: true,
      reverb: true,
    },
    duration: 2200,
    display: {
      title: t('special.desperate_kill'),
      subtitle: t('special.desperate_kill.subtitle'),
      color: '#ff4500',
      fontSize: 44,
      animation: 'slide',
    },
  },

  perfect_endgame: {
    type: 'perfect_endgame',
    particleEffect: {
      ...PARTICLE_PRESETS.blackhole,
      colors: ['#ffd700', '#ffdf00', '#fff700', '#ffa500', '#ffffff'],
    },
    visual: {
      screenShake: true,
      slowMotion: true,
      zoom: true,
      cameraMovement: true,
      colorFilter: '#ffd700',
    },
    audio: {
      soundEffect: 'perfect_endgame',
      volume: 1.0,
      echo: true,
      reverb: true,
    },
    duration: 3000,
    display: {
      title: t('special.perfect_endgame'),
      subtitle: t('special.perfect_endgame.subtitle'),
      color: '#ffd700',
      fontSize: 52,
      animation: 'zoom',
    },
  },

  edge_control: {
    type: 'edge_control',
    particleEffect: blendPresets('lightning', 'storm', 0.5),
    visual: {
      screenShake: true,
      slowMotion: false,
      zoom: false,
      cameraMovement: true,
      colorFilter: '#00ffff',
    },
    audio: {
      soundEffect: 'edge_control',
      volume: 0.85,
      echo: false,
    },
    duration: 1800,
    display: {
      title: t('special.edge_control'),
      subtitle: t('special.edge_control.subtitle'),
      color: '#00ffff',
      fontSize: 38,
      animation: 'slide',
    },
  },

  chain_reaction: {
    type: 'chain_reaction',
    particleEffect: blendPresets('lightning', 'explosion', 0.7),
    visual: {
      screenShake: true,
      slowMotion: true,
      zoom: false,
      cameraMovement: false,
      colorFilter: '#ffff00',
    },
    audio: {
      soundEffect: 'chain_reaction',
      volume: 0.9,
      echo: true,
    },
    duration: 2000,
    display: {
      title: t('special.chain_reaction'),
      subtitle: t('special.chain_reaction.subtitle'),
      color: '#ffff00',
      fontSize: 40,
      animation: 'explode',
    },
  },

  time_control: {
    type: 'time_control',
    particleEffect: {
      ...PARTICLE_PRESETS.blackhole,
      colors: ['#9932cc', '#8b00ff', '#ffffff', '#87ceeb', '#000000'],
    },
    visual: {
      screenShake: true,
      slowMotion: true,
      zoom: true,
      cameraMovement: true,
      colorFilter: '#9932cc',
    },
    audio: {
      soundEffect: 'time_control',
      volume: 1.0,
      echo: true,
      reverb: true,
    },
    duration: 3500,
    display: {
      title: t('special.time_control'),
      subtitle: t('special.time_control.subtitle'),
      color: '#9932cc',
      fontSize: 56,
      animation: 'zoom',
    },
  },
};

/**
 * 特殊招式檢測器
 */
export class SpecialMoveDetector {
  /**
   * 檢測移動是否觸發特殊招式
   */
  static detectSpecialMove(
    position: BoardPosition,
    flippedPositions: BoardPosition[],
    playerScore: number,
    opponentScore: number,
    moveNumber: number,
    totalMoves: number,
  ): SpecialMoveType[] {
    const triggeredMoves: SpecialMoveType[] = [];
    const flipCount = flippedPositions.length;
    const scoreDiff = playerScore - opponentScore;

    // 檢查每種特殊招式
    for (const [moveType, condition] of Object.entries(SPECIAL_MOVE_CONDITIONS)) {
      if (this.checkCondition(
        moveType as SpecialMoveType,
        condition,
        position,
        flipCount,
        scoreDiff,
        moveNumber,
        totalMoves,
        flippedPositions,
      )) {
        triggeredMoves.push(moveType as SpecialMoveType);
      }
    }

    return triggeredMoves;
  }

  /**
   * 檢查單個條件
   */
  private static checkCondition(
    moveType: SpecialMoveType,
    condition: SpecialMoveCondition,
    position: BoardPosition,
    flipCount: number,
    scoreDiff: number,
    moveNumber: number,
    totalMoves: number,
    flippedPositions: BoardPosition[],
  ): boolean {
    // 檢查翻轉數量
    if (condition.minFlipCount && flipCount < condition.minFlipCount) {
      return false;
    }

    // 檢查分數差距
    if (condition.scoreDifference) {
      const { min, max } = condition.scoreDifference;
      if (scoreDiff < min || scoreDiff > max) {
        return false;
      }
    }

    // 檢查位置類型
    if (condition.positionType) {
      if (!this.checkPositionType(position, condition.positionType)) {
        return false;
      }
    }

    // 檢查時機
    if (condition.timing) {
      if (!this.checkTiming(condition.timing, moveNumber, totalMoves)) {
        return false;
      }
    }

    // 檢查特殊條件
    if (condition.special) {
      if (!this.checkSpecialCondition(moveType, condition.special, {
        position,
        flipCount,
        scoreDiff,
        moveNumber,
        totalMoves,
        flippedPositions,
      })) {
        return false;
      }
    }

    return true;
  }

  /**
   * 檢查位置類型
   */
  private static checkPositionType(position: BoardPosition, type: 'corner' | 'edge' | 'center'): boolean {
    const row = Math.floor(position / 8);
    const col = position % 8;

    switch (type) {
      case 'corner':
        return (row === 0 || row === 7) && (col === 0 || col === 7);
      case 'edge':
        return row === 0 || row === 7 || col === 0 || col === 7;
      case 'center':
        return row >= 2 && row <= 5 && col >= 2 && col <= 5;
      default:
        return false;
    }
  }

  /**
   * 檢查時機
   */
  private static checkTiming(timing: 'early' | 'mid' | 'late', moveNumber: number, totalMoves: number): boolean {
    const progress = moveNumber / Math.max(totalMoves, 60); // 估計最大60步

    switch (timing) {
      case 'early':
        return progress < 0.3;
      case 'mid':
        return progress >= 0.3 && progress <= 0.7;
      case 'late':
        return progress > 0.7;
      default:
        return true;
    }
  }

  /**
   * 檢查特殊條件
   */
  private static checkSpecialCondition(
    moveType: SpecialMoveType,
    special: string,
    context: {
      position: BoardPosition;
      flipCount: number;
      scoreDiff: number;
      moveNumber: number;
      totalMoves: number;
      flippedPositions: BoardPosition[];
    },
  ): boolean {
    switch (special) {
      case 'near_endgame':
        return context.moveNumber > Math.max(context.totalMoves * 0.8, 40);

      case 'multiple_directions':
        // 檢查是否在多個方向翻轉棋子
        return this.checkMultipleDirections(context.position, context.flippedPositions);

      case 'ultra_rare':
        // 超稀有條件：大量翻轉 + 高分差
        return context.flipCount >= 15 && Math.abs(context.scoreDiff) > 10;

      default:
        return true;
    }
  }

  /**
   * 檢查是否在多個方向翻轉
   */
  private static checkMultipleDirections(position: BoardPosition, flippedPositions: BoardPosition[]): boolean {
    const directions = [
      [-1, -1], [-1, 0], [-1, 1],
      [0, -1],           [0, 1],
      [1, -1],  [1, 0],  [1, 1],
    ];

    const row = Math.floor(position / 8);
    const col = position % 8;
    let directionCount = 0;

    for (const [dr, dc] of directions) {
      if (dr === undefined || dc === undefined) {continue;}
      let found = false;
      let r = row + dr;
      let c = col + dc;

      while (r >= 0 && r < 8 && c >= 0 && c < 8) {
        const pos = r * 8 + c;
        if (flippedPositions.includes(pos)) {
          found = true;
          break;
        }
        r += dr!;
        c += dc!;
      }

      if (found) {
        directionCount++;
      }
    }

    return directionCount >= 3; // 至少3個方向
  }
}

/**
 * 特殊招式管理器
 */
export class SpecialMoveManager {
  private _activeEffects: Map<string, SpecialMoveEffect> = new Map();
  private _eventHandlers: ((effect: SpecialMoveEffect) => void)[] = [];

  /**
   * 觸發特殊招式
   */
  triggerSpecialMove(moveType: SpecialMoveType, position: { x: number; y: number }): void {
    const effect = SPECIAL_MOVE_EFFECTS[moveType];

    if (!effect) {
      console.warn(`Unknown special move type: ${moveType}`);
      return;
    }

    const effectId = `${moveType}_${Date.now()}`;
    this._activeEffects.set(effectId, effect);

    // 發送事件
    this._emitEffect(effect);

    // 設定移除時間
    setTimeout(() => {
      this._activeEffects.delete(effectId);
    }, effect.duration);

    console.log(`🎆 特殊招式觸發: ${effect.display.title}`);
  }

  /**
   * 獲取活動中的特效
   */
  getActiveEffects(): SpecialMoveEffect[] {
    return Array.from(this._activeEffects.values());
  }

  /**
   * 清除所有特效
   */
  clearAllEffects(): void {
    this._activeEffects.clear();
  }

  /**
   * 註冊特效事件處理器
   */
  addEventListener(handler: (effect: SpecialMoveEffect) => void): void {
    this._eventHandlers.push(handler);
  }

  /**
   * 移除特效事件處理器
   */
  removeEventListener(handler: (effect: SpecialMoveEffect) => void): void {
    const index = this._eventHandlers.indexOf(handler);
    if (index !== -1) {
      this._eventHandlers.splice(index, 1);
    }
  }

  /**
   * 發送特效事件
   */
  private _emitEffect(effect: SpecialMoveEffect): void {
    this._eventHandlers.forEach(handler => {
      try {
        handler(effect);
      } catch (error) {
        console.error('Special move effect handler error:', error);
      }
    });
  }
}